import React from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink as AtlasNavLink } from '@diligentcorp/atlas-web-component-global-nav/lib/react.js';
import { RoutedNavLink } from '@diligentcorp/atlas-web-component-global-nav/lib/react-router.js';
import AcademyIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Academy.js';
import { ContentAuthoringNavPanel } from './ContentAuthoringNavPanel';

interface ComplianceEducationNavItemsProps {
  canAccessContentManagement: boolean;
  canAccessTemplates: boolean;
  isInAuthoringPanel: boolean;
  onOpenAuthoringPanel: () => void;
  onCloseAuthoringPanel: () => void;
}

/**
 * Hierarchical Compliance Education app nav (Figma drill-down side panel).
 * Gated by `complianceEducationNavTreeEnabled` (dev and staging/preprod tiers). Rendered
 * inside hb-global-navigator when deployed — localhost uses LocalDevSideNav instead.
 *
 * Panel open/close state lives in GlobalNav so the main platform items hide while drilled in.
 */
export function ComplianceEducationNavItems({
  canAccessContentManagement,
  canAccessTemplates,
  isInAuthoringPanel,
  onOpenAuthoringPanel,
  onCloseAuthoringPanel,
}: ComplianceEducationNavItemsProps): React.ReactNode {
  const { t } = useTranslation();

  if (!canAccessContentManagement && !canAccessTemplates) {
    return null;
  }

  if (isInAuthoringPanel && canAccessTemplates) {
    return <ContentAuthoringNavPanel onBack={onCloseAuthoringPanel} />;
  }

  return (
    <>
      {canAccessContentManagement ? (
        <RoutedNavLink to="/manage-content" label={t('nav.compliance_education.customized_content')}>
          <AcademyIcon slot="icon" />
        </RoutedNavLink>
      ) : null}
      {canAccessTemplates ? (
        <AtlasNavLink
          as="button"
          label={t('nav.compliance_education.content_authoring')}
          isCurrent={isInAuthoringPanel}
          showExpandIcon
          onClick={onOpenAuthoringPanel}
        >
          <dil-ai-report-default-icon
            slot="icon"
            size="lg"
            variant="outlined"
            aria-hidden="true"
          />
        </AtlasNavLink>
      ) : null}
    </>
  );
}
